import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import Cookies from 'js-cookie';
import CustomFormRenderer from './CustomFormRenderer';
import DynamicTable from './DynamicTable';
import PremiumDatePicker from './common/PremiumDatePicker';
import { apiService } from '../services/apiService';
import { flattenComponents } from '../utils/formHelpers';

const PublicFormViewer = ({ form, initialEntries }) => {
    // --- CORE STATE ---
    const [userName, setUserName] = useState(() => Cookies.get('formUser') || "");
    const [isUserSet, setIsUserSet] = useState(() => !!Cookies.get('formUser'));
    const [tempName, setTempName] = useState("");
    const [entries, setEntries] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    // Project & Metadata States (global session)
    const [selectedProject, setSelectedProject] = useState("");
    const [sessionHeaders, setSessionHeaders] = useState([]);
    const [sessionFooters, setSessionFooters] = useState([]);

    // Developer Side: Track project names created in current session
    const [localProjectList, setLocalProjectList] = useState([]);

    // Batch Remarks States
    const [showSubmitModal, setShowSubmitModal] = useState(false);
    const [batchRemarks, setBatchRemarks] = useState("");

    // Delete Modal States
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [rowToDelete, setRowToDelete] = useState(null);

    // Image Preview State (Lightbox)
    const [previewImage, setPreviewImage] = useState(null);

    // --- OFFCANVAS STATE (Mobile Entry / Edit) ---
    const [showOffcanvas, setShowOffcanvas] = useState(false);
    const [offcanvasMode, setOffcanvasMode] = useState("ADD");
    const [editingEntry, setEditingEntry] = useState(null);
    const [formKey, setFormKey] = useState(0);
    const formInstanceRef = useRef(null);

    // --- OFFCANVAS CONTEXT STATE ---
    const [entryProject, setEntryProject] = useState("");
    const [isAddingNewProject, setIsAddingNewProject] = useState(false);
    const [newProjectNameInput, setNewProjectNameInput] = useState("");
    const [entryHeaders, setEntryHeaders] = useState([]);
    const [entryFooters, setEntryFooters] = useState([]);
    const [isEntryConfigLocked, setIsEntryConfigLocked] = useState(false);

    // Mix state
    const [mixList, setMixList] = useState([]);
    const [entryMix, setEntryMix] = useState("");
    const [isAddingNewMix, setIsAddingNewMix] = useState(false);
    const [newMixNameInput, setNewMixNameInput] = useState("");
    const [mixError, setMixError] = useState("");

    // --- INLINE ERROR STATES ---
    const [projectError, setProjectError] = useState("");
    const [footerError, setFooterError] = useState("");

    // --- SEARCHABLE DROPDOWN STATE ---
    const [showProjectDropdown, setShowProjectDropdown] = useState(false);
    const [projectSearch, setProjectSearch] = useState("");
    const dropdownRef = useRef(null);

    // --- REF TO TRACK DELETED TEMP IDS ---
    const deletedTempIds = useRef(new Set());

    // =============================================
    // DATA FETCHING
    // =============================================
    const loadEntries = useCallback(async (existingData = null) => {
        if (!form?.id) return;
        if (!existingData) setIsLoading(true);
        try {
            const allEntries = existingData || await apiService.fetchEntries(form.id);
            const processed = allEntries.map(item => ({
                ...item,
                ...(item.entry_data || {}),
                createdAt: item.created_date,
                createdBy: item.created_by,
                updatedAt: item.updated_date,
                updatedBy: item.updated_by,
                project_name: item.entry_data?.project_name || item.project_name || ""
            }));
            setEntries(processed);
        } catch (err) {
            console.error("History fetch failed", err);
        } finally {
            setIsLoading(false);
        }
    }, [form?.id]);

    // =============================================
    // EFFECTS
    // =============================================
    useEffect(() => {
        if (form?.id) {
            if (initialEntries && initialEntries.length >= 0) {
                loadEntries(initialEntries);
            } else {
                loadEntries();
            }
            if (form.project_details) {
                setSessionHeaders(form.project_details.headers || []);
                setSessionFooters(form.project_details.footers || []);
            }
        }
    }, [form?.id, loadEntries]);

    // Load mixes from localStorage
    useEffect(() => {
        try {
            const savedMixes = localStorage.getItem("my_form_mixes");
            if (savedMixes) {
                const parsedMixes = JSON.parse(savedMixes);
                const mixNames = parsedMixes
                    .filter(m => m.formId === form.id && m.name && m.name.trim() !== "")
                    .map(m => m.name);
                setMixList(mixNames);
            }
        } catch (e) {
            console.error("Error loading mixes:", e);
        }
    }, [form?.id]);

    // Click outside handler for dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowProjectDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Auto-load Headers/Footers when Project Changes
    useEffect(() => {
        if (editingEntry) return;
        if (!selectedProject || !form) return;

        const projectHistory = entries.filter(e => e.project_name === selectedProject);
        if (projectHistory.length > 0) {
            const latestEntry = [...projectHistory].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
            if (latestEntry) {
                if (latestEntry.session_headers) setSessionHeaders(latestEntry.session_headers);
                if (latestEntry.session_footers) setSessionFooters(latestEntry.session_footers);
            }
        } else {
            if (form.project_details) {
                setSessionHeaders(form.project_details.headers || []);
                setSessionFooters(form.project_details.footers || []);
            }
        }
    }, [selectedProject, entries, form, editingEntry]);

    // =============================================
    // COMPUTED VALUES
    // =============================================
    const projectOptions = useMemo(() => {
        const dbProjectNames = entries.map(e => e.project_name).filter(Boolean);
        const combined = [...dbProjectNames, ...localProjectList];
        return [...new Set(combined)];
    }, [entries, localProjectList]);

    const allComponents = useMemo(() => {
        if (!form?.schema?.components) return [];
        return flattenComponents(form.schema.components);
    }, [form]);

    const hasDrafts = entries.some(e => e.status === 0);

    // =============================================
    // USER SESSION HANDLERS
    // =============================================
    const handleStartSession = () => {
        if (!tempName.trim()) return;
        Cookies.set('formUser', tempName, { expires: 7 });
        setUserName(tempName);
        setIsUserSet(true);
    };

    const handleEditName = () => {
        Cookies.remove('formUser');
        setIsUserSet(false);
        setUserName("");
        setTempName("");
    };

    // =============================================
    // 1. HANDLE INLINE CREATE (Desktop "+" row)
    // =============================================
    const handleInlineCreate = async (rowData) => {
        const payload = {
            entry_data: {
                ...rowData,
                session_headers: form.project_details?.headers || [],
                session_footers: form.project_details?.footers || [],
            },
            form: form.id,
            created_by: userName,
            updated_by: userName,
            status: 0,
        };

        const tempId = Date.now();
        const optimisticEntry = {
            _rowId: tempId,
            id: tempId,
            ...rowData,
            project_name: rowData.project_name,
            mix_name: rowData.mix_name,
            created_date: rowData.created_date || new Date().toISOString().split("T")[0],
            createdAt: new Date().toISOString(),
            created_by: userName,
            status: 0,
            session_headers: form.project_details?.headers || [],
            session_footers: form.project_details?.footers || [],
        };

        setEntries(prev => [optimisticEntry, ...prev]);

        try {
            const response = await apiService.createEntry(payload);
            const realId = response ? (response._rowId || response.id || response._id) : null;

            if (deletedTempIds.current.has(tempId)) {
                if (realId) {
                    try { await apiService.deleteEntry(realId); } catch (err) { console.error("Failed to delete in-flight entry:", err); }
                }
                deletedTempIds.current.delete(tempId);
                return;
            }

            if (realId) {
                setEntries(prev =>
                    prev.map(entry =>
                        entry._rowId === tempId
                            ? { ...entry, _rowId: realId, id: realId, _id: realId }
                            : entry
                    )
                );
            }
        } catch (error) {
            console.error("Inline create failed", error);
            if (!deletedTempIds.current.has(tempId)) {
                setEntries(prev => prev.filter(entry => entry._rowId !== tempId));
            }
        }
    };

    // =============================================
    // 2. HANDLE INLINE EDIT (Pencil icon)
    // =============================================
    const handleInlineUpdate = async (updatedRowData) => {
        const rowId = updatedRowData.id || updatedRowData._id || updatedRowData._rowId;
        const originalRow = entries.find(e => e._rowId === rowId);
        const currentStatus = originalRow ? originalRow.status : 1;

        setEntries(prev =>
            prev.map(row => row._rowId === rowId ? { ...row, ...updatedRowData } : row)
        );

        const payload = {
            entry_data: { ...updatedRowData },
            form: form.id,
            updated_by: userName,
            status: currentStatus,
        };

        try {
            await apiService.updateEntry(rowId, payload);
        } catch (error) {
            console.error("Inline update failed", error);
            await loadEntries();
        }
    };

    // =============================================
    // 3. BATCH SUBMIT
    // =============================================
    const handleFinalSubmitAll = async () => {
        try {
            await apiService.submitAllDrafts(form.id, {
                updated_by: userName,
                remarks: batchRemarks
            });
            setShowSubmitModal(false);
            setBatchRemarks("");
            await loadEntries();
        } catch (error) {
            console.error("Failed to submit entries:", error);
        }
    };

    // =============================================
    // 4. DELETE HANDLERS
    // =============================================
    const handleDelete = (row) => {
        setRowToDelete(row);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!rowToDelete) return;
        try {
            await apiService.deleteEntry(rowToDelete._rowId);
            await loadEntries();
            setShowDeleteModal(false);
            setRowToDelete(null);
        } catch (error) {
            console.error("Delete failed:", error);
        }
    };

    // =============================================
    // 5. OFFCANVAS HANDLERS (Mobile Entry / Edit)
    // =============================================
    const openOffcanvas = (entry = null) => {
        setProjectError("");
        setFooterError("");
        setMixError("");
        setProjectSearch("");
        setShowProjectDropdown(false);

        if (entry) {
            setEditingEntry(entry);
            setOffcanvasMode("EDIT");
            setEntryProject(entry.project_name || "");
            setEntryMix(entry.mix_name || "");
            setEntryHeaders(entry.session_headers || form.project_details?.headers || []);
            setEntryFooters(entry.session_footers || form.project_details?.footers || []);
            setIsEntryConfigLocked(true);
        } else {
            setEditingEntry(null);
            setOffcanvasMode("ADD");
            setFormKey(p => p + 1);
            setEntryProject(selectedProject || "");
            setEntryMix("");
            setEntryHeaders(form.project_details?.headers || []);
            setEntryFooters(form.project_details?.footers || []);
            setIsEntryConfigLocked(false);
        }
        setShowOffcanvas(true);
    };

    const closeOffcanvas = () => {
        setShowOffcanvas(false);
        setEditingEntry(null);
    };

    const handleFormReady = useCallback((inst) => {
        formInstanceRef.current = inst;
    }, []);

    // --- OFFCANVAS METADATA HELPERS ---
    const updateEntryMetadata = (section, id, newValue) => {
        if (section === 'headers') {
            setEntryHeaders(prev => prev.map(item => item.id === id ? { ...item, value: newValue } : item));
        } else {
            setEntryFooters(prev => prev.map(item => item.id === id ? { ...item, value: newValue } : item));
        }
    };

    const handleEntryFooterImageUpload = (id, file) => {
        setFooterError("");
        if (file) {
            const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/jpg'];
            if (!validTypes.includes(file.type)) {
                setFooterError("Invalid format. Please upload JPG, PNG, or GIF.");
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => updateEntryMetadata('footers', id, reader.result);
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveEntryFooterImage = (id) => {
        updateEntryMetadata('footers', id, "");
        setFooterError("");
    };

    // --- PROJECT HANDLERS (inside Offcanvas) ---
    const handleSelectProject = (val) => {
        setEntryProject(val);
        setShowProjectDropdown(false);
        setProjectSearch("");
    };

    const handleConfirmNewProject = () => {
        const trimmedName = newProjectNameInput.trim();
        setProjectError("");

        if (trimmedName) {
            const exists = projectOptions.some(p => p.toLowerCase() === trimmedName.toLowerCase());
            if (exists) {
                setProjectError(`"${trimmedName}" already exists. Select it from the dropdown.`);
                return;
            }
            setLocalProjectList(prev => [...new Set([...prev, trimmedName])]);
            setEntryProject(trimmedName);
            setIsAddingNewProject(false);
            setNewProjectNameInput("");
        } else {
            setProjectError("Project name cannot be empty.");
        }
    };

    // --- MIX HANDLERS (inside Offcanvas) ---
    const handleConfirmNewMix = () => {
        const trimmed = newMixNameInput.trim();
        setMixError("");

        if (trimmed) {
            const exists = mixList.some(m => m.toLowerCase() === trimmed.toLowerCase());
            if (exists) {
                setMixError(`"${trimmed}" already exists.`);
                return;
            }
            const updatedMixList = [...mixList, trimmed];
            setMixList(updatedMixList);
            setEntryMix(trimmed);
            setIsAddingNewMix(false);
            setNewMixNameInput("");

            try {
                const currentStorage = JSON.parse(localStorage.getItem("my_form_mixes") || "[]");
                const newMixObj = { id: Date.now(), name: trimmed, formId: form.id };
                localStorage.setItem("my_form_mixes", JSON.stringify([...currentStorage, newMixObj]));
            } catch (e) {
                console.error("Failed to save mix to storage", e);
            }
        } else {
            setMixError("Mix name cannot be empty.");
        }
    };

    const formatMetadataSummary = (items) => {
        const filled = items.filter(i => i.value);
        if (filled.length === 0) return "None";
        return filled.map(i => `${i.label}: ${i.value}`).join(" | ");
    };

    // =============================================
    // 6. SAVE DRAFT FROM OFFCANVAS
    // =============================================
    const handleSaveDraft = () => {
        if (!formInstanceRef.current) return;

        setProjectError("");
        setMixError("");

        let isValid = true;
        if (!entryProject) {
            setProjectError("Please select or create a Project Name.");
            isValid = false;
        }
        if (!entryMix) {
            setMixError("Please select or create a Mix Name.");
            isValid = false;
        }
        if (!isValid) return;

        formInstanceRef.current
            .submit()
            .then(async (submission) => {
                const formData = submission.data;
                const payload = {
                    entry_data: {
                        ...formData,
                        project_name: entryProject,
                        mix_name: entryMix,
                        session_headers: entryHeaders,
                        session_footers: entryFooters,
                    },
                    form: form.id,
                    updated_by: userName,
                };
                try {
                    if (offcanvasMode === "EDIT" && editingEntry) {
                        await apiService.updateEntry(editingEntry._rowId, payload);
                        setShowOffcanvas(false);
                    } else {
                        payload.created_by = userName;
                        payload.updated_by = userName;
                        payload.status = 0;
                        await apiService.createEntry(payload);

                        setFormKey(p => p + 1);
                        setEntryProject("");
                        setEntryMix("");
                        setIsEntryConfigLocked(false);
                        setEntryHeaders(form.project_details?.headers || []);
                        setEntryFooters(form.project_details?.footers || []);
                    }
                    await loadEntries();
                } catch (error) {
                    console.error("Save failed:", error);
                }
            })
            .catch(err => console.log("Validation failed", err));
    };

    // =============================================
    // RENDER
    // =============================================

    if (!form) return (
        <div className="min-vh-100 d-flex align-items-center justify-content-center bg-white text-muted">
            <div className="text-center">
                <i className="bi bi-exclamation-triangle fs-1 mb-3"></i>
                <h5>Form not found.</h5>
            </div>
        </div>
    );

    if (!isUserSet) return (
        <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: '#f8f9fa' }}>
            <div className="card shadow-lg border-0 rounded-5 p-4 mx-3" style={{ maxWidth: '420px', width: '100%' }}>
                <div className="text-center mb-4">
                    <div className="bg-primary bg-opacity-10 text-primary rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                        <i className="bi bi-shield-lock-fill fs-3"></i>
                    </div>
                    <h4 className="fw-bold text-dark">{form.title}</h4>
                    <p className="text-muted small" style={{ fontSize: '12px' }}>Please identify yourself to begin the entry session.</p>
                </div>
                <div className="mb-4">
                    <label className="form-label small fw-bold text-secondary ms-1" style={{ fontSize: '14px' }}>Enter your full name</label>
                    <input
                        type="text"
                        className="form-control form-control-lg border-1 py-2 rounded-3 shadow-sm bg-white "
                        placeholder="e.g. John Doe"
                        style={{ borderRadius: '12px', fontSize: '15px' }}
                        value={tempName}
                        onChange={(e) => setTempName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleStartSession()}
                    />
                </div>
                <button className="btn btn-primary btn-lg w-100 fw-bold rounded-3 shadow-sm py-2" onClick={handleStartSession} disabled={!tempName.trim()}>
                    Continue <i className="bi bi-arrow-right ms-2"></i>
                </button>
            </div>
        </div>
    );

    return (
        <div className="min-vh-100 py-4 py-md-5" style={{ backgroundColor: '#f3f4f7' }}>
            <style>
                {`
                    .btn { transition: all 0.2s ease-in-out; }
                    .btn:hover { filter: brightness(1.1); transform: translateY(-1px); }
                    .btn:active { transform: scale(0.97); }
                    .animate-fade-in { animation: fadeIn 0.3s ease-out; }
                    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

                    .setup-summary-line { font-size: 13px; color: #495057; margin-bottom: 4px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
                    .setup-summary-label { font-weight: 700; color: #6c757d; margin-right: 6px; }

                    /* Image Hover Styles */
                    .img-upload-container { position: relative; display: inline-block; }
                    .img-upload-container:hover .btn-remove-image { display: flex; }
                    .btn-remove-image {
                        display: none; position: absolute; top: -8px; right: -8px;
                        width: 20px; height: 20px; background-color: #dc3545; color: white;
                        border-radius: 50%; align-items: center; justify-content: center;
                        font-size: 12px; cursor: pointer; border: 2px solid white;
                        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                    }
                    .btn-remove-image:hover { background-color: #bb2d3b; transform: scale(1.1); }

                    /* Custom Scrollbar */
                    .custom-scroll::-webkit-scrollbar { width: 5px; }
                    .custom-scroll::-webkit-scrollbar-track { background: #f1f1f1; }
                    .custom-scroll::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 10px; }
                    .custom-scroll::-webkit-scrollbar-thumb:hover { background: #a8a8a8; }
                    .hover-bg-light:hover { background-color: #f8f9fa; color: #0d6efd; }

                    /* Date input fix */
                    input[type="date"]::-webkit-inner-spin-button,
                    input[type="date"]::-webkit-calendar-picker-indicator { display: none; -webkit-appearance: none; }
                    input[type="date"] { position: relative; min-width: 120px; }
                    input[type="date"]::-webkit-datetime-edit { display: flex !important; overflow: visible !important; }
                    @media (pointer: coarse) {
                        input[type="date"]::-webkit-calendar-picker-indicator {
                            position: absolute; top: 0; left: 0; width: 100%; height: 100%;
                            opacity: 0; cursor: pointer; z-index: 10; display: block;
                        }
                    }
                    @media (pointer: fine) {
                        input[type="date"]::-webkit-calendar-picker-indicator {
                            opacity: 0; cursor: pointer; padding: 5px;
                        }
                    }
                `}
            </style>

            {/* =================== OFFCANVAS (Mobile Entry / Edit) =================== */}
            <div
                className={`offcanvas offcanvas-end ${showOffcanvas ? "show" : ""}`}
                style={{ width: "500px", visibility: showOffcanvas ? "visible" : "hidden", transition: "transform 0.3s ease-in-out", zIndex: 1045 }}
                tabIndex="-1"
            >
                <div className="offcanvas-header border-bottom py-2">
                    <h6 className="offcanvas-title fw-bold" style={{ fontSize: "15px" }}>
                        {offcanvasMode === "EDIT" ? "Edit Entry" : "New Entry"}
                    </h6>
                    <button type="button" className="btn-close text-reset" onClick={closeOffcanvas}></button>
                </div>
                <div className="offcanvas-body bg-light p-3">
                    {/* --- PROJECT CONTEXT SETUP CARD --- */}
                    <div className="card mb-4 border shadow-sm">
                        <div className="card-header bg-white py-2 d-flex justify-content-between align-items-center">
                            <span className="small fw-bold text-uppercase text-dark" style={{ fontSize: "13px" }}>Project Context</span>
                            {entryProject && (
                                <button className="btn btn-sm btn-link text-decoration-none p-0" onClick={() => setIsEntryConfigLocked(!isEntryConfigLocked)}>
                                    {isEntryConfigLocked ? "Change Setup" : "Hide Setup"}
                                </button>
                            )}
                        </div>
                        <div className="card-body">
                            {!isEntryConfigLocked ? (
                                <div className="animate-fade-in">
                                    {/* 1. PROJECT NAME */}
                                    <div className="mb-4">
                                        <label className="form-label fw-bold text-dark" style={{ fontSize: "13px" }}>Project Name</label>
                                        <div className="d-flex flex-column gap-1">
                                            <div className="d-flex gap-2" ref={dropdownRef}>
                                                {isAddingNewProject ? (
                                                    <div className="input-group">
                                                        <input
                                                            type="text"
                                                            className={`form-control ${projectError ? "border-danger" : ""}`}
                                                            placeholder="New Project..."
                                                            value={newProjectNameInput}
                                                            onChange={(e) => setNewProjectNameInput(e.target.value)}
                                                            list="existingProjects"
                                                            style={{ fontSize: "15px" }}
                                                        />
                                                        <datalist id="existingProjects">
                                                            <option value="General" />
                                                            {projectOptions.map((p, i) => <option key={i} value={p} />)}
                                                        </datalist>
                                                        <button className="btn btn-success" onClick={handleConfirmNewProject}><i className="bi bi-check fs-5"></i></button>
                                                        <button className="btn btn-outline-secondary" onClick={() => { setIsAddingNewProject(false); setProjectError(""); }}><i className="bi bi-x fs-5"></i></button>
                                                    </div>
                                                ) : (
                                                    <div className="input-group w-100">
                                                        <div
                                                            className={`form-control form-select d-flex justify-content-between align-items-center ${projectError ? "border-danger" : ""}`}
                                                            onClick={() => { setShowProjectDropdown(!showProjectDropdown); setProjectSearch(""); }}
                                                            style={{ cursor: "pointer", backgroundColor: "#fff", position: "relative", fontSize: "15px" }}
                                                        >
                                                            <span className="text-truncate">{entryProject || "-- Select Project --"}</span>
                                                        </div>
                                                        {showProjectDropdown && (
                                                            <div className="card position-absolute w-100 shadow mt-1 p-2 border-0" style={{ zIndex: 1050, top: "100%", left: 0, maxHeight: "250px" }}>
                                                                <input
                                                                    type="text" className="form-control mb-2" placeholder="Search projects..."
                                                                    value={projectSearch} onChange={(e) => setProjectSearch(e.target.value)}
                                                                    autoFocus onClick={(e) => e.stopPropagation()} style={{ fontSize: "14px" }}
                                                                />
                                                                <ul className="list-unstyled mb-0 custom-scroll" style={{ maxHeight: "180px", overflowY: "auto" }}>
                                                                    <li className="p-2 rounded hover-bg-light" onClick={() => handleSelectProject("General")} style={{ cursor: "pointer", fontSize: "15px" }}>
                                                                        <i className="bi bi-hash me-2 text-muted"></i>General
                                                                    </li>
                                                                    {projectOptions.filter(p => p.toLowerCase().includes(projectSearch.toLowerCase()) && p !== 'General').map((p, i) => (
                                                                        <li key={i} className="p-2 rounded hover-bg-light" onClick={() => handleSelectProject(p)} style={{ cursor: "pointer", fontSize: "15px" }}>
                                                                            <i className="bi bi-building me-2 text-muted"></i>{p}
                                                                        </li>
                                                                    ))}
                                                                    {projectOptions.length === 0 && <li className="p-2 text-muted text-center">No projects found</li>}
                                                                </ul>
                                                            </div>
                                                        )}
                                                        <button className="btn btn-outline-primary" onClick={() => setIsAddingNewProject(true)} title="Create New"><i className="bi bi-plus-lg"></i></button>
                                                    </div>
                                                )}
                                            </div>
                                            {projectError && <small className="text-danger fw-bold">{projectError}</small>}
                                        </div>
                                    </div>

                                    {/* 2. MIX NAME */}
                                    <div className="mb-3">
                                        <label className="form-label fw-bold text-dark" style={{ fontSize: "13px" }}>Mix Name</label>
                                        <div className="d-flex flex-column gap-1">
                                            <div className="d-flex gap-2">
                                                {isAddingNewMix ? (
                                                    <div className="input-group">
                                                        <input type="text" className={`form-control ${mixError ? "border-danger" : ""}`} placeholder="New Mix Name..." value={newMixNameInput} onChange={(e) => setNewMixNameInput(e.target.value)} style={{ fontSize: "15px" }} />
                                                        <button className="btn btn-success" onClick={handleConfirmNewMix}><i className="bi bi-check fs-5"></i></button>
                                                        <button className="btn btn-outline-secondary" onClick={() => setIsAddingNewMix(false)}><i className="bi bi-x fs-5"></i></button>
                                                    </div>
                                                ) : (
                                                    <div className="input-group w-100">
                                                        <select className="form-select" value={entryMix} onChange={(e) => setEntryMix(e.target.value)} style={{ fontSize: "15px" }}>
                                                            <option value="">-- Select Mix --</option>
                                                            {mixList.map((m, i) => <option key={i} value={m}>{m}</option>)}
                                                        </select>
                                                        <button className="btn btn-outline-primary" onClick={() => setIsAddingNewMix(true)} title="Create New Mix"><i className="bi bi-plus-lg"></i></button>
                                                    </div>
                                                )}
                                            </div>
                                            {mixError && <small className="text-danger fw-bold">{mixError}</small>}
                                        </div>
                                    </div>

                                    {/* Editable Headers */}
                                    {entryProject && entryHeaders.length > 0 && (
                                        <div className="mb-3 bg-light p-2 rounded border">
                                            <h6 className="small fw-bold mb-2 text-muted">Headers</h6>
                                            {entryHeaders.map((h) => (
                                                <div className="row g-1 mb-1" key={h.id}>
                                                    <div className="col-4 small text-truncate align-self-center">{h.label}:</div>
                                                    <div className="col-8">
                                                        <input className="form-control form-control-sm" value={h.value || ""} onChange={(e) => updateEntryMetadata("headers", h.id, e.target.value)} />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Editable Footers */}
                                    {entryProject && entryFooters.length > 0 && (
                                        <div className="mb-2 bg-light p-2 rounded border">
                                            <h6 className="small fw-bold mb-2 text-muted">Footers</h6>
                                            {entryFooters.map((f) => (
                                                <div className="row g-1 mb-1" key={f.id}>
                                                    <div className="col-4 small align-self-center">{f.type === "image" ? "Sig/Img" : "Note"}:</div>
                                                    <div className="col-8">
                                                        {f.type === "image" ? (
                                                            <div className="d-flex flex-column gap-2">
                                                                {f.value && (
                                                                    <div className="img-upload-container">
                                                                        <img src={f.value} alt="Uploaded" className="border rounded p-1 bg-white" style={{ height: "30px", cursor: "zoom-in", objectFit: "contain" }} onClick={() => setPreviewImage(f.value)} />
                                                                        <div className="btn-remove-image" onClick={() => handleRemoveEntryFooterImage(f.id)} title="Remove Image"><i className="bi bi-x"></i></div>
                                                                    </div>
                                                                )}
                                                                <input type="file" className="form-control form-control-sm" onChange={(e) => handleEntryFooterImageUpload(f.id, e.target.files[0])} accept="image/png, image/jpeg, image/jpg, image/gif" />
                                                                {footerError && <small className="text-danger fw-bold">{footerError}</small>}
                                                            </div>
                                                        ) : (
                                                            <input className="form-control form-control-sm" value={f.value || ""} onChange={(e) => updateEntryMetadata("footers", f.id, e.target.value)} />
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                /* LOCKED SUMMARY VIEW */
                                <div className="animate-fade-in">
                                    <div className="setup-summary-line">
                                        <span className="setup-summary-label">Project:</span>
                                        <span className="fw-bold text-primary">{entryProject}</span>
                                    </div>
                                    <div className="setup-summary-line">
                                        <span className="setup-summary-label">Mix Name:</span>
                                        <span className="fw-bold text-dark">{entryMix || "None"}</span>
                                    </div>
                                    <div className="setup-summary-line">
                                        <span className="setup-summary-label">Headers:</span>
                                        <span className="text-dark">{formatMetadataSummary(entryHeaders)}</span>
                                    </div>
                                    <div className="setup-summary-line mb-0">
                                        <span className="setup-summary-label">Footers:</span>
                                        <span className="text-dark">
                                            {entryFooters.filter(f => f.value).map((f, idx) => (
                                                <span key={idx} className="me-2 d-inline-flex align-items-center">
                                                    {f.type === "image" ? (
                                                        <img src={f.value} alt="Sig" style={{ height: "15px", border: "1px solid #ccc", cursor: "pointer" }} onClick={() => setPreviewImage(f.value)} />
                                                    ) : (
                                                        <span>{f.value}</span>
                                                    )}
                                                    {idx < entryFooters.filter(x => x.value).length - 1 && <span className="mx-1">|</span>}
                                                </span>
                                            ))}
                                            {entryFooters.filter(f => f.value).length === 0 && "None"}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Form Renderer */}
                    <CustomFormRenderer
                        key={formKey}
                        schema={form.schema}
                        initialData={editingEntry || {}}
                        onFormReady={handleFormReady}
                    />
                </div>
                <div className="offcanvas-footer border-top p-2 bg-white d-flex justify-content-between align-items-center">
                    <button className="btn btn-danger btn-sm px-3 shadow-sm" onClick={closeOffcanvas}>Close</button>
                    <button className="btn btn-primary btn-sm px-3 shadow-sm" onClick={handleSaveDraft}>
                        {offcanvasMode === "EDIT" ? "Update Entry" : "Save Draft"}
                    </button>
                </div>
            </div>
            {showOffcanvas && <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} onClick={closeOffcanvas}></div>}

            {/* =================== MAIN CONTENT =================== */}
            <div className="container-fluid px-4 px-lg-5">

                {/* Header Section */}
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
                    <div>
                        <h3 className="fw-bold mb-0 text-dark d-flex align-items-center">
                            <i className="bi bi-file-earmark-text text-primary me-3"></i>
                            {form.title}
                            {selectedProject && (
                                <span className="ms-3 badge bg-white text-primary border shadow-sm fw-bold" style={{ fontSize: '14px' }}>
                                    <i className="bi bi-building me-2"></i>{selectedProject}
                                </span>
                            )}
                        </h3>
                        <div className="d-flex align-items-center gap-2 text-muted mt-2" style={{ fontSize: '14px' }}>
                            <span className="fw-medium">All Records</span>
                            <div className="d-flex align-items-center gap-1 bg-white px-2 py-1 rounded-3 border shadow-sm">
                                <i className="bi bi-person-circle text-success"></i>
                                <span className="fw-bold text-dark">{userName}</span>
                                <button className="btn btn-link p-0 text-muted border-0" onClick={handleEditName}>
                                    <i className="bi bi-pencil-square" style={{ fontSize: '12px' }}></i>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* DynamicTable — the sole view */}
                <div className="animate-fade-in mx-auto">
                    {isLoading ? (
                        <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '60vh' }}>
                            <div className="spinner-border text-success" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <DynamicTable
                            data={entries}
                            isReportMode={false}
                            columnOrder={allComponents.slice(0, 5).map(c => c.key)}
                            allComponents={allComponents}
                            customColumns={[]}
                            projectDetails={{
                                headers: sessionHeaders,
                                footers: sessionFooters
                            }}
                            themeColor="#4F46E5"
                            formTitle={form.title}
                            onEdit={handleInlineUpdate}
                            onDelete={handleDelete}
                            onOpenOffcanvas={(row) => openOffcanvas(row)}
                            onSaveInline={handleInlineCreate}
                            onBatchSubmit={() => setShowSubmitModal(true)}
                            hasDrafts={hasDrafts}
                            selectedProject={selectedProject}
                            projectList={projectOptions}
                            mixList={mixList}
                            includeHeaderFooter={true}
                            onToggleHeaderFooter={() => { }}
                        />
                    )}
                </div>
            </div>

            {/* =================== MODALS =================== */}

            {/* MODAL: LIGHTBOX IMAGE ZOOM */}
            {previewImage && (
                <div
                    className="modal-backdrop d-flex align-items-center justify-content-center"
                    style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 3000 }}
                    onClick={() => setPreviewImage(null)}
                >
                    <div className="position-relative animate-fade-in" style={{ maxWidth: '90%', maxHeight: '90%' }}>
                        <button className="btn btn-dark rounded-circle position-absolute top-0 end-0 m-3 shadow" onClick={() => setPreviewImage(null)} style={{ zIndex: 3001, width: '40px', height: '40px' }}>
                            <i className="bi bi-x-lg"></i>
                        </button>
                        <img src={previewImage} alt="Full View" style={{ maxWidth: '100%', maxHeight: '90vh', borderRadius: '8px', boxShadow: '0 10px 25px rgba(0,0,0,0.5)' }} onClick={(e) => e.stopPropagation()} />
                    </div>
                </div>
            )}

            {/* MODAL: FINAL BATCH REMARKS */}
            {showSubmitModal && (
                <div className="modal-backdrop d-flex align-items-center justify-content-center" style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 2000 }}>
                    <div className="card border-0 shadow-lg rounded-4 p-4 mx-3" style={{ maxWidth: '450px', width: '90%' }}>
                        <h5 className="fw-bold mb-3 text-dark">Final batch Remarks</h5>
                        <p className="text-muted small">Please enter remarks for this batch. This will appear in the Master Report.</p>
                        <textarea className="form-control mb-4" rows="3" placeholder="e.g., Daily progress for today's session..." value={batchRemarks} onChange={(e) => setBatchRemarks(e.target.value)}></textarea>
                        <div className="d-flex gap-2">
                            <button className="btn btn-light flex-grow-1 border fw-bold" onClick={() => setShowSubmitModal(false)}>Cancel</button>
                            <button className="btn btn-success flex-grow-1 fw-bold shadow" onClick={handleFinalSubmitAll} disabled={!batchRemarks.trim()}>Confirm & Submit</button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL: DELETE CONFIRMATION */}
            {showDeleteModal && (
                <div className="modal-backdrop d-flex align-items-center justify-content-center" style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 2000 }}>
                    <div className="card border-2 shadow-lg rounded-4 p-3 mx-3 animate__animated animate__zoomIn" style={{ maxWidth: '400px', width: '90%' }}>
                        <div className="text-center mb-4">
                            <i className="bi bi-trash3-fill text-danger fs-3 mb-2"></i>
                            <h5 className="fw-bold">Delete Record?</h5>
                            <p className="text-muted mb-0 small">This record will be permanently removed from your local history.</p>
                        </div>
                        <div className="d-flex gap-3">
                            <button className="btn btn-light flex-grow-1 border" onClick={() => setShowDeleteModal(false)}>Cancel</button>
                            <button className="btn btn-danger flex-grow-1 rounded-3 shadow py-2 fw-bold" onClick={confirmDelete}>Delete</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PublicFormViewer;